import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { cn } from '../lib/utils';
import {
  ListChecks, BarChart2, ShieldOff, Code2, FileText, Coins
} from 'lucide-react';

const features = [
  {
    icon: ListChecks,
    title: 'Dynamic Field Extraction',
    desc: 'You define exactly which fields to extract — vendor name, invoice number, due date, line items, anything. No fixed schema. The API finds what you ask for.',
    tag: 'Your fields, your rules',
    color: 'text-primary',
    bg: 'bg-primary/8',
  },
  {
    icon: BarChart2,
    title: 'Confidence Scores',
    desc: 'Every extracted field comes with a confidence score from 0.0 to 1.0. Know immediately which results to trust and which to flag for review.',
    tag: '0.0 – 1.0 per field',
    color: 'text-accent',
    bg: 'bg-accent/8',
  },
  {
    icon: ShieldOff,
    title: 'Privacy-First',
    desc: 'All processing happens on our own server — no cloud AI, no third-party APIs. Documents are processed in-memory and never written to disk.',
    tag: 'Fully offline · No storage',
    color: 'text-primary',
    bg: 'bg-primary/8',
  },
  {
    icon: FileText,
    title: 'PDF & Image Support',
    desc: 'Upload PDFs (up to 10 pages), PNG, JPEG, WEBP, or TIFF — up to 20 MB per file. Multi-page PDFs are processed page by page automatically.',
    tag: 'PDF · PNG · JPEG · TIFF',
    color: 'text-accent',
    bg: 'bg-accent/8',
  },
  {
    icon: Code2,
    title: 'Simple REST API',
    desc: 'One POST endpoint, multipart file upload, JSON response. No SDK required. Works with curl, Python requests, or any HTTP client.',
    tag: 'Developer-first',
    color: 'text-primary',
    bg: 'bg-primary/8',
  },
  {
    icon: Coins,
    title: 'Credit System',
    desc: 'Raw OCR costs 1 credit per page. Field extraction costs 3 credits per page. Start with 10 free credits — no credit card needed.',
    tag: '1 cr · 3 cr per page',
    color: 'text-accent',
    bg: 'bg-accent/8',
  },
];

export default function FeaturesSection() {
  const [ref, visible] = useScrollAnimation();

  return (
    <section id='features' ref={ref} className={cn('py-24 bg-surface reveal', visible && 'revealed')}>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        {/* Header */}
        <div className='text-center max-w-2xl mx-auto mb-16'>
          <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-accent/10 text-accent-dark border border-accent/20 mb-4'>
            Capabilities
          </span>
          <h2 className='font-heading font-bold text-4xl lg:text-5xl text-foreground mb-4'>
            Built for invoice data extraction
          </h2>
          <p className='text-muted-foreground text-lg leading-relaxed'>
            A focused API that does one thing well — pull structured fields from invoices and documents.
          </p>
        </div>

        {/* Grid */}
        <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-6'>
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className={cn(
                  'bg-card border border-border rounded-2xl p-6 shadow-card hover:shadow-primary group transition-shadow duration-300 flex flex-col reveal',
                  `reveal-delay-${(i % 3) + 1}`,
                  visible && 'revealed'
                )}
              >
                <div className={cn('w-11 h-11 rounded-xl flex items-center justify-center mb-4', f.bg)}>
                  <Icon className={cn('w-5 h-5', f.color)} strokeWidth={2} />
                </div>
                <h3 className='font-heading font-semibold text-lg text-foreground mb-2'>{f.title}</h3>
                <p className='text-sm text-muted-foreground leading-relaxed flex-1'>{f.desc}</p>
                <div className='mt-4'>
                  <span className='inline-flex items-center text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-full'>
                    {f.tag}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
